// Cartiglio (title block) defaults. Shared by the main-process store (built-in
// seed / normalisation) and the renderer (drawing). A cartiglio is a flat list
// of free-form title objects bound to one company+paper+orientation, plus the
// shared content (company, address, legal, logo) those objects seed from.

import type {
  Cartiglio,
  CartiglioSource,
  PaperFormat,
  TextI18n,
  TitleObject,
  TitleObjectRole,
} from './types';

export const DEFAULT_ADDRESS_LINES = [
  'Via Barovier, 8',
  '35031 Abano T. (PD) ITALY',
  'Tel. +39 049-8912047',
  'www.stenal.it',
  'commerciale@stenal.it',
];
export const DEFAULT_LEGAL = {
  copyright:
    'Il presente disegno è stato realizzato dalla ditta STENAL, la quale ne detiene ' +
    "l'esclusiva proprietà ed i diritti di Copyright. La divulgazione, la riproduzione, " +
    'la realizzazione anche se parziale, senza autorizzazione della STENAL, è ' +
    "severamente vietata ai sensi dell'articolo 2698 del C.C.",
  disclaimer:
    'Il presente disegno è stato elaborato sulla base di misure fornite dal Cliente. ' +
    'La Stenal non si assume alcuna responsabilità qualora queste non corrispondessero ' +
    'alla situazione reale.',
  signature: 'Firma del Cliente per accettazione',
};

export const DEFAULT_COMPANY = 'STENAL';
const DEFAULT_PAPER: PaperFormat = 'A4';
const DEFAULT_ORIENTATION: 'portrait' | 'landscape' = 'landscape';
// A4 landscape in mm — the default sheet the fallback cartiglio is laid out for.
const DEFAULT_W = 297, DEFAULT_H = 210;

/** The built-in Stenal cartiglio, used as the seed / offline fallback. */
export function defaultCartiglio(): Cartiglio {
  return {
    id: 'cart-default',
    name: 'Standard Stenal',
    isDefault: true,
    company: DEFAULT_COMPANY,
    addressLines: [...DEFAULT_ADDRESS_LINES],
    legal: { ...DEFAULT_LEGAL },
    paper: DEFAULT_PAPER,
    orientation: DEFAULT_ORIENTATION,
    objects: defaultTitleObjects(DEFAULT_PAPER, DEFAULT_W, DEFAULT_H),
  };
}

/** Fill any missing pieces of a (possibly partial) cartiglio from the default,
 *  so old or hand-edited Baserow rows still render. */
export function normalizeCartiglio(raw: Partial<Cartiglio> | undefined, rowId?: number): Cartiglio {
  const d = defaultCartiglio();
  const paper = raw?.paper ?? d.paper;
  const orientation = raw?.orientation ?? d.orientation;
  const c: Cartiglio = {
    id: raw?.id || d.id,
    name: raw?.name || d.name,
    isDefault: !!raw?.isDefault,
    company: raw?.company ?? d.company,
    addressLines: raw?.addressLines?.length ? raw.addressLines : d.addressLines,
    logoDataUri: raw?.logoDataUri,
    legal: { ...d.legal, ...(raw?.legal || {}) },
    paper,
    orientation,
    objects: raw?.objects?.length ? upgradeVersioneField(raw.objects) : d.objects,
  };
  if (rowId != null) c.rowId = rowId;
  return c;
}

/** VERSIONE used to be a `blank` field — a heading with nothing under it, since
 *  no drawing property fed it. Now `DrawingMeta.versione` does, so a stored
 *  cartiglio's blank VERSIONE cell is re-bound on load; without this the
 *  property would only ever show in title blocks generated after this change,
 *  which is none of the ones already in Baserow. A cell someone deliberately
 *  re-worded is left alone — only the standard heading is recognised. */
function upgradeVersioneField(objects: TitleObject[]): TitleObject[] {
  const headings = new Set(['VERSIONE', ...Object.values(LABELS_I18N.VERSIONE)].map((s) => s.toUpperCase()));
  return objects.map((o) =>
    o.role === 'field' && (o.source ?? 'blank') === 'blank' && headings.has((o.word || '').trim().toUpperCase())
      ? { ...o, source: 'versione' as const }
      : o);
}

// ---- Object-based model ----

const TB_FONT = 'Arial, sans-serif';
const TB_INK = '#2f2a28';
const TB_LABEL = '#6e1414';
const TB_VALUE = '#111111';
const TB_ADDR = '#555555';

interface CellOpts { source?: CartiglioSource; literal?: string; word?: string; wordI18n?: TextI18n; wordFontMm?: number; wordColor?: string; valueFontMm?: number; valueColor?: string; }
function titleCell(id: string, role: TitleObjectRole, x: number, y: number, w: number, h: number, o: CellOpts = {}): TitleObject {
  return {
    id, role, x, y, w, h,
    border: { top: 0.35, right: 0.35, bottom: 0.35, left: 0.35, color: TB_INK },
    word: o.word ?? '', wordI18n: o.wordI18n, wordFontMm: o.wordFontMm ?? 2.2, wordFont: TB_FONT, wordColor: o.wordColor ?? TB_LABEL,
    source: o.source, literal: o.literal,
    valueFontMm: o.valueFontMm ?? 2.6, valueFont: TB_FONT, valueColor: o.valueColor ?? TB_VALUE,
  };
}

/** Standard field/heading labels in the three supported languages, so the
 *  built-in cartiglio reads correctly whatever a drawing's language is. */
const LABELS_I18N: Record<string, TextI18n> = {
  PROGETTO: { it: 'PROGETTO', en: 'PROJECT', fr: 'PROJET' },
  CLIENTE: { it: 'CLIENTE', en: 'CLIENT', fr: 'CLIENT' },
  VERSIONE: { it: 'VERSIONE', en: 'VERSION', fr: 'VERSION' },
  DATA: { it: 'DATA', en: 'DATE', fr: 'DATE' },
  FOGLIO: { it: 'FOGLIO', en: 'SHEET', fr: 'FEUILLE' },
  SCALA: { it: 'SCALA', en: 'SCALE', fr: 'ÉCHELLE' },
};
const SIGNATURE_I18N: TextI18n = {
  it: 'Firma del Cliente per accettazione',
  en: "Client's signature for acceptance",
  fr: 'Signature du client pour acceptation',
};

/** The six standard field blocks: [key, WORD, source, default width mm]. */
const FIELD_SPECS: Array<[string, string, CartiglioSource, number]> = [
  ['progetto', 'PROGETTO', 'descrizione', 32],
  ['cliente', 'CLIENTE', 'commessa', 30],
  ['versione', 'VERSIONE', 'versione', 18],
  ['data', 'DATA', 'data', 20],
  ['foglio', 'FOGLIO', 'foglio', 16],
  ['scala', 'SCALA', 'scala', 16],
];

/** A default free-form title-block layout for a page of `W`×`H` mm: a top row
 *  for A4/A3/custom, a right strip otherwise, reproducing the classic look. */
export function defaultTitleObjects(paper: PaperFormat, W: number, H: number): TitleObject[] {
  const objs: TitleObject[] = [];
  const addr = { literal: DEFAULT_ADDRESS_LINES.join('\n'), valueFontMm: 1.9, valueColor: TB_ADDR };
  const legalOpt = (text: string): CellOpts => ({ literal: text, wordFontMm: 0, valueFontMm: 1.7, valueColor: TB_ADDR });
  const top = paper === 'A4' || paper === 'A3' || paper === 'custom';
  if (top) {
    const y = 3, ch = 18;
    objs.push(titleCell('logo', 'logo', 4, y, 40, ch));
    objs.push(titleCell('address', 'address', 46, y, 34, ch, addr));
    const total = FIELD_SPECS.reduce((a, s) => a + s[3], 0);
    const minX = 84;
    let x = W - 4 - total, scale = 1;
    if (x < minX) { scale = (W - 4 - minX) / total; x = minX; }
    for (const [key, word, source, w] of FIELD_SPECS) { const cw = w * scale; objs.push(titleCell(key, 'field', x, y, cw, ch, { word, wordI18n: LABELS_I18N[word], source })); x += cw; }
    const fy = H - 20, fw = W - 8;
    objs.push(titleCell('copyright', 'text', 4, fy, fw * 0.5, 16, legalOpt(DEFAULT_LEGAL.copyright)));
    objs.push(titleCell('disclaimer', 'text', 4 + fw * 0.5 + 2, fy, fw * 0.28, 16, legalOpt(DEFAULT_LEGAL.disclaimer)));
    objs.push(titleCell('firma', 'text', 4 + fw * 0.8 + 2, fy, fw * 0.2 - 2, 16, { word: DEFAULT_LEGAL.signature, wordI18n: SIGNATURE_I18N, wordFontMm: 1.8, wordColor: TB_VALUE, valueFontMm: 0 }));
  } else {
    const sw = 58, x0 = W - sw - 4;
    let y = 4;
    objs.push(titleCell('logo', 'logo', x0, y, sw, 16)); y += 18;
    objs.push(titleCell('address', 'address', x0, y, sw, 24, addr)); y += 26;
    for (const [key, word, source] of FIELD_SPECS) { objs.push(titleCell(key, 'field', x0, y, sw, 14, { word, wordI18n: LABELS_I18N[word], source })); y += 14; }
    const fy = H - 20, fw = x0 - 8;
    objs.push(titleCell('copyright', 'text', 4, fy, fw * 0.6, 16, legalOpt(DEFAULT_LEGAL.copyright)));
    objs.push(titleCell('disclaimer', 'text', 4 + fw * 0.6 + 2, fy, fw * 0.38, 16, legalOpt(DEFAULT_LEGAL.disclaimer)));
  }
  return objs;
}

/** Build a full object-model cartiglio bound to a company + paper + orientation. */
export function objectCartiglio(company: string, paper: PaperFormat, orientation: 'portrait' | 'landscape', W: number, H: number): Cartiglio {
  const base = defaultCartiglio();
  return {
    ...base,
    id: `cart-${company}-${paper}-${orientation}`.toLowerCase(),
    name: `${company} ${paper} ${orientation === 'portrait' ? 'V' : 'O'}`,
    company, paper, orientation,
    isDefault: false,
    objects: defaultTitleObjects(paper, W, H),
  };
}

/** A single catalogue preset (dragged from the panel to add an object). */
export function titleObjectPreset(kind: string, x: number, y: number): TitleObject {
  const spec = FIELD_SPECS.find((s) => s[0] === kind);
  if (spec) return titleCell(`${kind}-${x | 0}-${y | 0}`, 'field', x, y, Math.max(24, spec[3]), 16, { word: spec[1], wordI18n: LABELS_I18N[spec[1]], source: spec[2] });
  if (kind === 'logo') return titleCell(`logo-${x | 0}`, 'logo', x, y, 40, 18);
  if (kind === 'address') return titleCell(`address-${x | 0}`, 'address', x, y, 34, 18, { literal: DEFAULT_ADDRESS_LINES.join('\n'), valueFontMm: 1.9, valueColor: TB_ADDR });
  if (kind === 'firma') return titleCell(`firma-${x | 0}`, 'text', x, y, 50, 16, { word: DEFAULT_LEGAL.signature, wordI18n: SIGNATURE_I18N, wordFontMm: 1.8, wordColor: TB_VALUE, valueFontMm: 0 });
  // generic legal / text block
  return titleCell(`text-${x | 0}`, 'text', x, y, 60, 16, { literal: 'Testo', valueFontMm: 1.8, valueColor: TB_ADDR });
}

/** Catalogue items shown in the title-block editor's left panel; each label is
 *  a dictionary key, shown in the UI's language. */
export const TITLE_CATALOG: Array<{ kind: string; label: string }> = [
  { kind: 'logo', label: 'Logo' },
  { kind: 'address', label: 'Address' },
  { kind: 'progetto', label: 'Project' },
  { kind: 'cliente', label: 'Customer' },
  { kind: 'versione', label: 'Version' },
  { kind: 'data', label: 'Date' },
  { kind: 'foglio', label: 'Sheet' },
  { kind: 'scala', label: 'Scale' },
  { kind: 'text', label: 'Legal text' },
  { kind: 'firma', label: 'Signature' },
];
